import { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, AlertCircle, Languages } from 'lucide-react';
import type { TranslationLanguage } from './TranslationsForm';
import type { TranslationFieldConfig, TranslationData } from './translations-data';

/**
 * Статус заполненности языка
 */
type LanguageStatus = 'complete' | 'partial' | 'empty';

interface LanguageStatusInfo {
  status: LanguageStatus;
  filledCount: number;
  requiredFilledCount: number;
  totalFields: number;
  requiredFields: number;
  hasErrors: boolean;
}

interface LanguageSwitcherProps {
  languages: TranslationLanguage[];
  activeLanguage: string;
  onLanguageChange: (code: string) => void;
  fields: TranslationFieldConfig[];
  value: TranslationData;
  errors?: Record<string, Record<string, string>>;
  className?: string;
  /** Вариант отображения: inline (в строку), compact (компактный с popover) */
  variant?: 'inline' | 'compact' | 'auto';
  /** Максимум языков для inline отображения */
  maxInlineLanguages?: number;
}

/**
 * Адаптивный переключатель языков с индикацией заполненности полей.
 *
 * - При малом количестве языков показывает их в строку
 * - При большом количестве языков использует Popover с прокруткой
 * - Показывает статус заполненности для каждого языка
 */
export function LanguageSwitcher({
  languages,
  activeLanguage,
  onLanguageChange,
  fields,
  value,
  errors,
  className,
  variant = 'auto',
  maxInlineLanguages = 5,
}: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getLanguageStatus = useCallback(
    (langCode: string): LanguageStatusInfo => {
      const langData = value[langCode] ?? {};
      const langErrors = errors?.[langCode] ?? {};

      const filledCount = fields.filter((f) => langData[f.name]?.trim()).length;
      const requiredFields = fields.filter((f) => f.required);
      const requiredFilledCount = requiredFields.filter(
        (f) => langData[f.name]?.trim()
      ).length;

      let status: LanguageStatus = 'empty';
      if (filledCount === fields.length && requiredFilledCount === requiredFields.length) {
        status = 'complete';
      } else if (filledCount > 0) {
        status = 'partial';
      }

      return {
        status,
        filledCount,
        requiredFilledCount,
        totalFields: fields.length,
        requiredFields: requiredFields.length,
        hasErrors: Object.keys(langErrors).length > 0,
      };
    },
    [value, fields, errors]
  );

  const languageStatuses = useMemo(() => {
    return languages.reduce(
      (acc, lang) => {
        acc[lang.code] = getLanguageStatus(lang.code);
        return acc;
      },
      {} as Record<string, LanguageStatusInfo>
    );
  }, [languages, getLanguageStatus]);

  const overallStats = useMemo(() => {
    const stats = Object.values(languageStatuses);
    return {
      complete: stats.filter((s) => s.status === 'complete').length,
      partial: stats.filter((s) => s.status === 'partial').length,
      empty: stats.filter((s) => s.status === 'empty').length,
      total: stats.length,
      hasErrors: stats.some((s) => s.hasErrors),
    };
  }, [languageStatuses]);

  const shouldUsePopover =
    variant === 'compact' ||
    (variant === 'auto' && languages.length > maxInlineLanguages);

  const activeLanguageData = languages.find((l) => l.code === activeLanguage);
  const activeStatus = languageStatuses[activeLanguage];

  const handleSelect = (code: string) => {
    onLanguageChange(code);
    setIsOpen(false);
  };

  if (shouldUsePopover) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              className="min-w-[180px] justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{activeLanguageData?.flag}</span>
                <span className="truncate">{activeLanguageData?.nativeName}</span>
                <StatusBadge status={activeStatus} compact />
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0" align="start">
            <div className="p-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Languages className="h-4 w-4" />
                  <span>Языки перевода</span>
                </div>
                <OverallStatusBadge stats={overallStats} />
              </div>
            </div>
            <ScrollArea className="max-h-[300px]">
              <div className="p-2">
                <div className="grid grid-cols-2 gap-1">
                  {languages.map((lang) => {
                    const status = languageStatuses[lang.code];
                    const isActive = lang.code === activeLanguage;

                    return (
                      <button
                        type="button"
                        key={lang.code}
                        onClick={() => handleSelect(lang.code)}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                          isActive && 'bg-accent text-accent-foreground',
                          status.hasErrors && 'ring-1 ring-destructive'
                        )}
                      >
                        <span className="text-base shrink-0">{lang.flag}</span>
                        <span className="truncate flex-1 text-left">
                          {lang.nativeName}
                        </span>
                        <StatusIndicator status={status} />
                        {isActive && (
                          <Check className="h-4 w-4 shrink-0 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Inline вариант для малого количества языков
  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {languages.map((lang) => {
        const status = languageStatuses[lang.code];
        const isActive = lang.code === activeLanguage;

        return (
          <Tooltip key={lang.code}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onLanguageChange(lang.code)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  'border',
                  'hover:bg-accent hover:text-accent-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
                    : 'bg-background border-input',
                  status.hasErrors && !isActive && 'border-destructive'
                )}
              >
                <span className="text-sm">{lang.flag}</span>
                <span className="hidden sm:inline">{lang.nativeName}</span>
                <span className="sm:hidden uppercase">{lang.code}</span>
                <StatusIndicator status={status} isActive={isActive} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <LanguageTooltipContent lang={lang} status={status} />
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * Индикатор статуса языка (иконка/бейдж)
 */
function StatusIndicator({
  status,
  isActive = false,
}: {
  status: LanguageStatusInfo;
  isActive?: boolean;
}) {
  if (status.hasErrors) {
    return (
      <AlertCircle
        className={cn(
          'h-3.5 w-3.5',
          isActive ? 'text-primary-foreground' : 'text-destructive'
        )}
      />
    );
  }

  if (status.status === 'complete') {
    return (
      <div
        className={cn(
          'h-2 w-2 rounded-full',
          isActive ? 'bg-primary-foreground' : 'bg-green-500'
        )}
      />
    );
  }

  if (status.status === 'partial') {
    return (
      <Badge
        variant="secondary"
        className={cn(
          'h-4 px-1 text-[10px] font-normal',
          isActive
            ? 'bg-primary-foreground/20 text-primary-foreground'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        )}
      >
        {status.filledCount}/{status.totalFields}
      </Badge>
    );
  }

  return (
    <div
      className={cn(
        'h-2 w-2 rounded-full',
        isActive ? 'bg-primary-foreground/40' : 'bg-muted-foreground/30'
      )}
    />
  );
}

/**
 * Компактный бейдж статуса
 */
function StatusBadge({
  status,
  compact = false,
}: {
  status: LanguageStatusInfo;
  compact?: boolean;
}) {
  if (status.hasErrors) {
    return (
      <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
        <AlertCircle className="h-3 w-3 mr-0.5" />
        {!compact && 'Ошибки'}
      </Badge>
    );
  }

  if (status.status === 'complete') {
    return (
      <Badge className="h-5 px-1.5 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <Check className="h-3 w-3" />
      </Badge>
    );
  }

  return (
    <Badge
      variant="secondary"
      className="h-5 px-1.5 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
    >
      {status.filledCount}/{status.totalFields}
    </Badge>
  );
}

/**
 * Общий статус всех языков
 */
function OverallStatusBadge({
  stats,
}: {
  stats: {
    complete: number;
    partial: number;
    empty: number;
    total: number;
    hasErrors: boolean;
  };
}) {
  if (stats.hasErrors) {
    return (
      <Badge variant="destructive" className="text-[10px]">
        Есть ошибки
      </Badge>
    );
  }

  if (stats.complete === stats.total) {
    return (
      <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        Все заполнены
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className="text-[10px]">
      {stats.complete}/{stats.total} готово
    </Badge>
  );
}

/**
 * Контент тултипа для языка
 */
function LanguageTooltipContent({
  lang,
  status,
}: {
  lang: TranslationLanguage;
  status: LanguageStatusInfo;
}) {
  return (
    <div className="space-y-1">
      <div className="font-medium">{lang.nativeName}</div>
      <div className="text-xs opacity-80">
        {status.status === 'complete' && 'Все поля заполнены'}
        {status.status === 'partial' && (
          <>
            Заполнено: {status.filledCount} из {status.totalFields}
            {status.requiredFields > 0 && (
              <span className="block">
                Обязательные: {status.requiredFilledCount} из{' '}
                {status.requiredFields}
              </span>
            )}
          </>
        )}
        {status.status === 'empty' && 'Не заполнено'}
      </div>
      {status.hasErrors && (
        <div className="text-xs text-destructive">Есть ошибки валидации</div>
      )}
    </div>
  );
}
